import React, { useEffect, useRef, useState } from 'react';
import { Lock, ArrowRight, ChevronDown, ChevronUp, Compass } from 'lucide-react';
import { colors } from '../styles/colors';
import { MINING_PROGRAM } from '../config/miningProgram';

const SESSION_KEY = 'mpp_unlocked';
const HERO_IMG = '/reserves-hero.webp';

const NAV_SECTIONS = [
  { id: 'reserve-growth-flywheel', label: 'Overview' },
  { id: 'decision-framework', label: 'Framework' },
  { id: 'yield-components', label: 'Yield' },
  { id: 'component-4', label: 'Performance' },
  { id: 'az-reserve-flywheel', label: 'Flywheel' },
  { id: 'annual-yield-calculation', label: 'Calculation' },
  { id: 'contribution-index-yield', label: 'Reference' },
];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

// ---------------------------------------------------------------------------
// Shared building blocks (Arizore visual system)
// ---------------------------------------------------------------------------
const Eyebrow = ({ children, center = false }) => (
  <div className={`flex items-center gap-3 mb-4 ${center ? 'justify-center' : ''}`}>
    <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
    <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>{children}</span>
    {center && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
  </div>
);

const SectionHeading = ({ eyebrow, title, sub, center = true }) => (
  <div className={`${center ? 'mx-auto text-center max-w-3xl' : 'max-w-3xl'} mb-12 sm:mb-14`}>
    {eyebrow && <Eyebrow center={center}>{eyebrow}</Eyebrow>}
    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h2>
    {sub && <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{sub}</p>}
  </div>
);

const SectionShell = ({ id, bg = '#050505', children }) => (
  <section id={id} className="py-16 sm:py-24 scroll-mt-20" style={{ backgroundColor: bg }}>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

const Card = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl p-6 sm:p-7 h-full flex flex-col ${className}`}
    style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
  >
    {children}
  </div>
);

const NumberTag = ({ children }) => (
  <span className="text-xs font-semibold tracking-[0.18em]" style={{ color: colors.teal }}>{children}</span>
);

const WeightBadge = ({ children }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ backgroundColor: `${colors.teal}14`, color: colors.teal, border: `1px solid ${colors.teal}30` }}>
    {children}
  </span>
);

const Note = ({ title, children }) => (
  <div className="rounded-xl p-5" style={{ background: `${colors.teal}0D`, border: `1px solid ${colors.teal}26` }}>
    {title && <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: colors.teal }}>{title}</p>}
    <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.75 }}>{children}</p>
  </div>
);

// Two-column reference table (label / value)
const KVTable = ({ head, rows }) => (
  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
    <div className="grid grid-cols-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.16em]" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
      <span style={{ color: colors.white, opacity: 0.55 }}>{head[0]}</span>
      <span className="text-right" style={{ color: colors.white, opacity: 0.55 }}>{head[1]}</span>
    </div>
    {rows.map(([k, v]) => (
      <div key={k} className="grid grid-cols-2 px-4 py-2.5 text-sm" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{ color: colors.white, opacity: 0.8 }}>{k}</span>
        <span className="text-right font-semibold" style={{ color: colors.teal }}>{v}</span>
      </div>
    ))}
  </div>
);

const FormulaStrip = ({ children }) => (
  <div className="rounded-xl px-5 py-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center text-sm font-medium" style={{ background: `${colors.teal}10`, border: `1px solid ${colors.teal}30`, color: colors.white }}>
    {children}
  </div>
);
const Op = ({ children }) => <span style={{ color: colors.teal }}>{children}</span>;

// ---------------------------------------------------------------------------
// Password gate (client-side, controlled by MINING_PROGRAM.gateEnabled)
// ---------------------------------------------------------------------------
const PasswordGate = ({ onUnlock }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (value === MINING_PROGRAM.password) {
      sessionStorage.setItem(SESSION_KEY, 'true');
      onUnlock();
    } else {
      setError(true);
    }
  };

  return (
    <div
      data-testid="mpp-gate"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(180deg, #000000 0%, #03090C 60%, #07141A 100%)' }}
    >
      <div aria-hidden="true" className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(55% 55% at 50% 35%, rgba(0,128,128,0.12) 0%, rgba(0,0,0,0) 70%)' }} />
      <div
        className="relative w-full max-w-md rounded-2xl p-8 sm:p-10 text-center"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: `1px solid ${colors.teal}26`, boxShadow: '0 40px 90px -40px rgba(0,0,0,0.85)' }}
      >
        <img src="/az-reserve-logo-white-stacked.png" alt="AZ Reserve" className="h-14 w-auto max-w-[70%] mx-auto mb-7" />
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: `${colors.teal}16`, border: `1px solid ${colors.teal}40` }}>
          <Lock className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
        </div>
        <h1 className="text-xl sm:text-2xl font-bold mb-2 tracking-tight" style={{ color: colors.white }}>Mining Participation Program</h1>
        <p className="text-sm leading-relaxed mb-7" style={{ color: colors.white, opacity: 0.6 }}>
          Enter your access password to continue.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="password"
            data-testid="mpp-password-input"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(false); }}
            placeholder="Password"
            autoFocus
            className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-colors duration-200"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: `1px solid ${error ? '#E05656' : 'rgba(255,255,255,0.15)'}`, color: colors.white }}
          />
          {error && (
            <p data-testid="mpp-gate-error" className="text-xs text-left" style={{ color: '#E05656' }}>
              Incorrect password. Please try again.
            </p>
          )}
          <button
            type="submit"
            data-testid="mpp-unlock-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
            style={{ backgroundColor: colors.teal, color: colors.white }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Access Program
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Content data (verbatim from the AZ Reserve Mining Participation Program deck)
// ---------------------------------------------------------------------------
const flywheelSteps = ['More mines join', 'More assets pledged', 'More bullion delivered', 'Greater reserve profit', 'Larger participation pool'];

const decisionVariables = [
  { n: '01', title: 'Financing Requirement', q: 'How much financing do we require?', items: ['Minimum pledge percentage', 'Minimum AZG issuance requirement'] },
  { n: '02', title: 'Reserve Contribution', q: 'How much additional reserve value are we willing to contribute?', items: ['Yield multiplier', 'Reserve participation level'] },
  { n: '03', title: 'Commitment Duration', q: 'How long are we prepared to commit?', items: ['Yield multiplier', 'Reserve participation multiplier'] },
  { n: '04', title: 'Delivery Performance', q: 'How effectively can we manage bullion deliveries?', items: ['Yield multiplier', 'Reserve participation multiplier'] },
];

const yieldComponents = [
  { n: '01', title: 'Stake Value', label: 'Total Tokens Staked', weight: '25%' },
  { n: '02', title: 'Pledge Commitment', label: '% of Resources Pledged', weight: '25%' },
  { n: '03', title: 'Staking Term', label: 'Term Length', weight: '15%' },
  { n: '04', title: 'Delivery Performance', label: 'Delivery Performance Score', weight: '35%' },
];

const components123 = [
  {
    n: 'Component 1', title: 'Stake Score', tagline: 'Rewards scale',
    note: 'The higher the AZG stake value, the greater the Stake Score contributed to the Contribution Index.',
    head: ['Stake Value', 'Stake Score'],
    rows: [['Below $10M', '50'], ['$10M–$25M', '65'], ['$25M–$50M', '80'], ['$50M–$75M', '90'], ['$75M–$100M', '100'], ['$100M+', '120']],
  },
  {
    n: 'Component 2', title: 'Pledge Commitment Score', tagline: 'Rewards pledge %',
    note: 'The greater the reserve commitment, the greater the Pledge Commitment Score.',
    head: ['Pledge', 'Score'],
    rows: [['20%', '70'], ['25%', '80'], ['30%', '90'], ['35%', '100'], ['40%', '110'], ['45%', '120'], ['50%', '130']],
  },
  {
    n: 'Component 3', title: 'Staking Term Score', tagline: 'Rewards certainty',
    note: 'Longer participation periods increase the Staking Term Score.',
    head: ['Participation Term', 'Score'],
    rows: [['6 Months', '70'], ['12 Months', '90'], ['24 Months', '110'], ['36 Months', '130']],
  },
];

const volumeScore = [['Below 90%', '0'], ['90–94.99%', '40'], ['95–99.99%', '75'], ['100–109.99%', '100'], ['110–124.99%', '105'], ['125–144.99%', '110'], ['145–174.99%', '115'], ['175–199.99%', '120'], ['200%+', '130']];
const timingScore = [['60+ Days Early', '120'], ['30–59 Days Early', '115'], ['1–29 Days Early', '110'], ['Contract Date', '100'], ['1–30 Days Late', '75'], ['31–60 Days Late', '50'], ['61–90 Days Late', '25'], ['90+ Days Late', '0']];
const deliveryExample = [['Contract', '1,000 oz'], ['Due', '1 July 2027'], ['Actual', '1,100 oz'], ['Delivered', '15 June 2027'], ['Volume Score', '105'], ['Timing Score', '110']];

const reservePrograms = [
  { tag: 'Product 1', title: 'AZ Reserve Finance Program', outcome: 'Generate Liquidity' },
  { tag: 'Product 2', title: 'AZ Reserve Growth Participation Program', outcome: 'Generate Yield' },
];

const yieldAssumptions = [['AZG Staked', '$80M'], ['Pledge Commitment', '40%'], ['Staking Term', '12 Months'], ['Contracted Delivery', '1,000 oz'], ['Due Date', '1 July 2027']];
const scoringWeighting = [['Stake Value', '100', '25%', '25.00'], ['Pledge Commitment', '110', '25%', '27.50'], ['Staking Term', '90', '15%', '13.50']];
const baseWeightedScore = '66.00';
const mineComparisonHead = ['Mine', 'Delivery Result', 'Delivery Timing', 'Volume Score', 'Timing Score', 'Delivery Score', 'Total Contribution Index', 'Annual Yield'];
const mineComparison = [
  ['Mine A', '1,450 oz Delivered', '5 Days Early', '115', '110', '39.3', '105.4', '10.00%'],
  ['Mine B', '1,000 oz Delivered', 'On Time', '100', '100', '35.0', '101.0', '9.00%'],
  ['Mine C', '900 oz Delivered', '5 Days Late', '40', '75', '20.1', '86.12', '6.00%'],
];

const yieldReference = [
  ['Below 60', '3.00%'], ['60–64', '3.50%'], ['65–69', '4.00%'], ['70–74', '4.50%'],
  ['75–79', '5.00%'], ['80–84', '5.50%'], ['85–89', '6.00%'], ['90–94', '7.00%'],
  ['95–99', '8.00%'], ['100–104', '9.00%'], ['105–109', '10.00%'], ['110–114', '11.00%'],
  ['115–119', '12.00%'], ['120–124', '13.00%'], ['125–129', '14.00%'],
];

// ---------------------------------------------------------------------------
// Sticky in-page navigation (appears after hero, scroll-spy active state)
// ---------------------------------------------------------------------------
const StickyNav = ({ active, show }) => {
  const navRef = useRef(null);
  useEffect(() => {
    const c = navRef.current;
    if (!c) return;
    const btn = c.querySelector(`[data-nav="${active}"]`);
    if (btn) c.scrollTo({ left: btn.offsetLeft - 24, behavior: 'smooth' });
  }, [active]);

  return (
    <nav
      data-testid="mpp-sticky-nav"
      className={`fixed left-0 right-0 top-0 z-40 transition-all duration-300 ${show ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-full pointer-events-none'}`}
      style={{ backgroundColor: 'rgba(4,4,4,0.9)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={navRef}
          className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto py-3 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {NAV_SECTIONS.map((s) => {
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                data-nav={s.id}
                data-testid={`mpp-nav-${s.id}`}
                onClick={() => scrollToId(s.id)}
                className="whitespace-nowrap px-3.5 py-1.5 rounded-full text-[11px] sm:text-xs uppercase tracking-[0.14em] font-medium transition-all duration-200"
                style={{
                  color: isActive ? colors.teal : 'rgba(255,255,255,0.6)',
                  backgroundColor: isActive ? `${colors.teal}16` : 'transparent',
                  border: `1px solid ${isActive ? `${colors.teal}38` : 'transparent'}`,
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Page content (video-first)
// ---------------------------------------------------------------------------
const PageContent = () => {
  const [showNav, setShowNav] = useState(false);
  const [active, setActive] = useState('program-video');

  useEffect(() => {
    const onScroll = () => setShowNav(window.scrollY > window.innerHeight * 0.65);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-25% 0px -65% 0px', threshold: 0 }
    );
    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
  <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
    <StickyNav active={active} show={showNav} />

    {/* Floating back-to-top */}
    <button
      type="button"
      data-testid="mpp-back-to-top"
      aria-label="Back to top"
      onClick={scrollToTop}
      className={`fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 ${showNav ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-3 pointer-events-none'}`}
      style={{ backgroundColor: colors.teal, color: colors.white, boxShadow: '0 12px 32px -8px rgba(0,128,128,0.6)' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <ChevronUp className="w-5 h-5" strokeWidth={2} />
    </button>

    {/* Brand bar */}
    <header className="border-b" style={{ borderColor: 'rgba(255,255,255,0.06)', backgroundColor: '#040404' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <img src="/logo-white-header.png" alt="Arizore" className="h-9 sm:h-11 w-auto" />
        <span className="hidden sm:inline-flex items-center text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full" style={{ color: colors.teal, border: `1px solid ${colors.teal}30`, backgroundColor: `${colors.teal}10` }}>
          Mining Participation Program
        </span>
      </div>
    </header>

    {/* 1 — HERO */}
    <section className="relative overflow-hidden" style={{ backgroundColor: '#000000' }}>
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.62) 45%, rgba(5,5,5,0.96) 100%)' }} />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 60% at 50% 42%, rgba(0,128,128,0.18) 0%, rgba(0,0,0,0) 70%)' }} />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-32 sm:pb-28 text-center">
        <Eyebrow center>AZ Reserve</Eyebrow>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.06] mb-6" style={{ color: colors.white }}>
          Mining Participation Program
        </h1>
        <p className="text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto mb-9" style={{ color: 'rgba(255,255,255,0.82)' }}>
          {"\u201CThe greater the contribution to reserve growth, the greater the participation in reserve profits.\u201D"}
        </p>
        <div className="flex justify-center">
          <button
            type="button"
            data-testid="mpp-hero-framework-btn"
            onClick={() => scrollToId('decision-framework')}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
            style={{ backgroundColor: colors.teal, color: colors.white }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <Compass className="w-4 h-4" strokeWidth={2} />
            Explore Framework
          </button>
        </div>
        <div className="mt-12 flex justify-center">
          <button type="button" aria-label="Scroll to content" onClick={() => scrollToId('reserve-growth-flywheel')} className="opacity-50 hover:opacity-90 transition-opacity duration-300">
            <ChevronDown className="w-6 h-6 animate-bounce" style={{ color: colors.teal }} />
          </button>
        </div>
      </div>
    </section>

    {/* Reserve Growth Flywheel (deck Page 01) — first content section after hero */}
    <SectionShell id="reserve-growth-flywheel">
      <SectionHeading
        eyebrow="Overview"
        title="Reserve Growth Flywheel"
        sub="As each mine delivers their contracted bullion to our vaults, auditors verify Arizore's enhanced value and allocate the profit to the AZ Reserve."
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4 mb-8">
        {flywheelSteps.map((step, i) => (
          <Card key={step} className="text-center !p-5">
            <NumberTag>{`0${i + 1}`}</NumberTag>
            <p className="mt-2 text-sm font-semibold leading-snug" style={{ color: colors.white }}>{step}</p>
          </Card>
        ))}
      </div>
      <div className="rounded-2xl p-6 sm:p-8 text-center" style={{ background: `${colors.teal}0D`, border: `1px solid ${colors.teal}26` }}>
        <p className="text-3xl sm:text-4xl font-bold mb-1" style={{ color: colors.teal }}>15%</p>
        <p className="text-sm uppercase tracking-[0.18em] font-semibold mb-3" style={{ color: colors.white }}>Of reserve profits allocated to the participation pool</p>
        <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.7 }}>
          Distributed quarterly in USDT and allocated according to the participation framework.
        </p>
        <p className="text-xs mt-3" style={{ color: colors.white, opacity: 0.45 }}>
          *Distributions commence following an initial deferment period of 45 days.
        </p>
      </div>
    </SectionShell>

    {/* 3 — PARTICIPANT DECISION FRAMEWORK (deck Page 02) */}
    <SectionShell id="decision-framework" bg="#040404">
      <SectionHeading
        eyebrow="Framework"
        title="Participant Decision Framework"
        sub="Every participating mine controls four key variables which determine its participation within the AZ Reserve Mining Participation Program."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5 mb-8">
        {decisionVariables.map((v) => (
          <Card key={v.n}>
            <NumberTag>{v.n}</NumberTag>
            <h3 className="mt-2 mb-2 text-lg font-semibold tracking-tight" style={{ color: colors.white }}>{v.title}</h3>
            <p className="text-sm italic mb-4" style={{ color: colors.white, opacity: 0.7 }}>{v.q}</p>
            <p className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: colors.teal }}>Determines</p>
            <ul className="space-y-1.5">
              {v.items.map((it) => (
                <li key={it} className="flex items-center gap-2 text-sm" style={{ color: colors.white, opacity: 0.75 }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.teal }} />{it}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
      <Note title="Participation is determined by contribution">
        The greater the reserve contribution, commitment duration and delivery performance, the greater the participation opportunity.
      </Note>
    </SectionShell>

    {/* 4 — THE 4 SCORING COMPONENTS (deck Page 03) */}
    <SectionShell id="yield-components">
      <SectionHeading
        eyebrow="Scoring"
        title="The 4 Scoring Components"
        sub="Four weighted components each contribute a score. Combined, they produce the final Contribution Index for a participating mine."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-8">
        {yieldComponents.map((c) => (
          <Card key={c.n}>
            <div className="flex items-center justify-between mb-3">
              <NumberTag>{c.n}</NumberTag>
              <WeightBadge>{c.weight}</WeightBadge>
            </div>
            <h3 className="text-lg font-semibold tracking-tight mb-1.5" style={{ color: colors.white }}>{c.title}</h3>
            <p className="text-sm" style={{ color: colors.teal, opacity: 0.9 }}>{c.label}</p>
          </Card>
        ))}
      </div>
      <Note title="Why delivery performance matters">
        As reserve profits are generated through bullion deliveries, Delivery Performance carries the highest weighting within the scoring framework.
      </Note>
    </SectionShell>

    {/* 5 — COMPONENTS 1–3 (deck Page 04) */}
    <SectionShell id="components-1-3" bg="#040404">
      <SectionHeading
        eyebrow="Detail"
        title="Components 1–3"
        sub="Each of the first three components contributes a weighted score based on stake value, pledge commitment and staking term."
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 mb-8">
        {components123.map((c) => (
          <Card key={c.n}>
            <NumberTag>{c.n}</NumberTag>
            <h3 className="mt-2 text-lg font-semibold tracking-tight" style={{ color: colors.white }}>{c.title}</h3>
            <p className="text-[11px] uppercase tracking-[0.14em] mb-1.5 mt-1" style={{ color: colors.teal }}>{c.tagline}</p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: colors.white, opacity: 0.65 }}>{c.note}</p>
            <div className="mt-auto"><KVTable head={c.head} rows={c.rows} /></div>
          </Card>
        ))}
      </div>
      <Note title="How the scores combine">
        Each component contributes a weighted score. Together with the Delivery Performance Score (Component 4), these combine to produce a final Contribution Index, which determines the Annual Yield using the reference table later on this page.
      </Note>
    </SectionShell>

    {/* 6 — COMPONENT 4: DELIVERY PERFORMANCE SCORE (deck Pages 05–06) */}
    <SectionShell id="component-4">
      <SectionHeading
        eyebrow="Detail"
        title="Component 4 — Delivery Performance Score"
        sub="Measured against the contracted delivery schedules, both weight and date, there is an ability to deliver more and to deliver it earlier to achieve a higher score."
      />
      <div className="mb-8">
        <FormulaStrip>
          <strong style={{ color: colors.white }}>Delivery Performance Score</strong> <Op>=</Op> Volume Score (50%) <Op>+</Op> Timing Score (50%)
        </FormulaStrip>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 mb-6">
        <Card>
          <h3 className="text-lg font-semibold tracking-tight mb-1" style={{ color: colors.white }}>Part 1 — Volume Score</h3>
          <p className="text-xs mb-4" style={{ color: colors.white, opacity: 0.5 }}>Contract example: 1,000 oz due</p>
          <KVTable head={['Actual Delivery', 'Score']} rows={volumeScore} />
          <div className="mt-4"><Note title="Key principle">Delivering above the contracted bullion amount increases the Volume Score and therefore improves the overall Delivery Performance Score.</Note></div>
        </Card>
        <Card>
          <h3 className="text-lg font-semibold tracking-tight mb-1" style={{ color: colors.white }}>Part 2 — Timing Score</h3>
          <p className="text-xs mb-4" style={{ color: colors.white, opacity: 0.5 }}>Contract example: delivery due 1 July 2027</p>
          <KVTable head={['Delivery Date', 'Score']} rows={timingScore} />
          <div className="mt-4"><Note title="Key principle">Delivering bullion ahead of schedule improves the Timing Score and therefore increases the overall Delivery Performance Score.</Note></div>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 items-center rounded-2xl p-6 sm:p-7" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-3" style={{ color: colors.teal }}>Example</p>
          <KVTable head={['Item', 'Value']} rows={deliveryExample} />
        </div>
        <FormulaStrip>
          (105 × 50%) <Op>+</Op> (110 × 50%) <Op>=</Op> 107.5 <Op>×</Op> 35% <Op>=</Op> <strong style={{ color: colors.teal }}>37.6</strong>
          <span className="w-full text-xs" style={{ color: colors.white, opacity: 0.55 }}>Total Delivery Contribution</span>
        </FormulaStrip>
      </div>
    </SectionShell>

    {/* 7 — THE AZ RESERVE FLYWHEEL (deck Page 07) */}
    <SectionShell id="az-reserve-flywheel" bg="#040404">
      <SectionHeading
        eyebrow="Ecosystem"
        title="The AZ Reserve Flywheel"
        sub="From Verification To Participation — AZ Reserve evolves from a reserve verification and token issuance platform into a reserve-backed participation network where contribution drives yield."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5 mb-8">
        {reservePrograms.map((p) => (
          <Card key={p.tag}>
            <span className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: colors.teal }}>{p.tag}</span>
            <h3 className="mt-2 mb-1.5 text-lg font-semibold tracking-tight" style={{ color: colors.white }}>{p.title}</h3>
            <p className="text-sm uppercase tracking-[0.12em]" style={{ color: colors.white, opacity: 0.6 }}>{p.outcome}</p>
          </Card>
        ))}
      </div>
      <div className="rounded-2xl p-6 sm:p-8 text-center mb-8" style={{ background: `${colors.teal}0D`, border: `1px solid ${colors.teal}26` }}>
        <p className="text-3xl sm:text-4xl font-bold mb-1" style={{ color: colors.teal }}>15%</p>
        <p className="text-sm uppercase tracking-[0.18em] font-semibold" style={{ color: colors.white }}>Of reserve profits allocated to the mining participation program</p>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-4 text-center" style={{ color: colors.teal }}>Why participate?</p>
        <p className="text-sm text-center mb-5" style={{ color: colors.white, opacity: 0.65 }}>
          Together these programs provide mine operators with two reasons to join the ecosystem:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          <Card className="text-center !p-5"><p className="text-sm font-semibold uppercase tracking-[0.1em]" style={{ color: colors.white }}>Access Capital</p></Card>
          <Card className="text-center !p-5"><p className="text-sm font-semibold uppercase tracking-[0.1em]" style={{ color: colors.white }}>Earn An Ongoing Reserve Growth Return</p></Card>
        </div>
      </div>
    </SectionShell>

    {/* 8 — ANNUAL YIELD CALCULATION (deck Page 08) */}
    <SectionShell id="annual-yield-calculation">
      <SectionHeading eyebrow="Calculation" title="Annual Yield Calculation" sub="Final Contribution Formula" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-3" style={{ color: colors.teal }}>Example Assumptions</p>
          <KVTable head={['Item', 'Assumption']} rows={yieldAssumptions} />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-3" style={{ color: colors.teal }}>Score Conversion</p>
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="grid grid-cols-[1.4fr_0.7fr_0.7fr_1fr] px-4 py-2.5 text-[10px] uppercase tracking-[0.14em]" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
              <span style={{ color: colors.white, opacity: 0.55 }}>Metric</span>
              <span className="text-right" style={{ color: colors.white, opacity: 0.55 }}>Score</span>
              <span className="text-right" style={{ color: colors.white, opacity: 0.55 }}>Weight</span>
              <span className="text-right" style={{ color: colors.white, opacity: 0.55 }}>Points</span>
            </div>
            {scoringWeighting.map(([metric, score, weight, points]) => (
              <div key={metric} className="grid grid-cols-[1.4fr_0.7fr_0.7fr_1fr] px-4 py-2.5 text-sm" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <span style={{ color: colors.white, opacity: 0.8 }}>{metric}</span>
                <span className="text-right tabular-nums" style={{ color: colors.white, opacity: 0.7 }}>{score}</span>
                <span className="text-right tabular-nums" style={{ color: colors.white, opacity: 0.7 }}>{weight}</span>
                <span className="text-right font-semibold tabular-nums" style={{ color: colors.teal }}>{points}</span>
              </div>
            ))}
            <div className="grid grid-cols-[2.1fr_1fr] px-4 py-2.5 text-sm" style={{ borderTop: `1px solid ${colors.teal}33`, backgroundColor: `${colors.teal}0D` }}>
              <span className="uppercase tracking-[0.12em] text-[11px] font-semibold self-center" style={{ color: colors.white, opacity: 0.7 }}>Base weighted score (before delivery)</span>
              <span className="text-right font-bold tabular-nums" style={{ color: colors.teal }}>{baseWeightedScore}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 mb-6">
        <FormulaStrip>
          <strong style={{ color: colors.white }}>Delivery Score</strong> <Op>=</Op> (Volume Score <Op>×</Op> 50%) <Op>+</Op> (Timing Score <Op>×</Op> 50%)
        </FormulaStrip>
        <FormulaStrip>
          <strong style={{ color: colors.white }}>Contribution Index</strong> <Op>=</Op> (Stake <Op>×</Op> 25%) <Op>+</Op> (Pledge <Op>×</Op> 25%) <Op>+</Op> (Term <Op>×</Op> 15%) <Op>+</Op> (Delivery <Op>×</Op> 35%)
        </FormulaStrip>
      </div>

      <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-3" style={{ color: colors.teal }}>Example Mine Comparison</p>
      <div className="overflow-x-auto rounded-xl mb-6" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
        <table className="w-full min-w-[880px] text-sm border-collapse">
          <thead>
            <tr style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
              {mineComparisonHead.map((h, i) => (
                <th key={h} className={`px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium ${i === 0 ? 'text-left' : 'text-right'}`} style={{ color: colors.white, opacity: 0.55 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mineComparison.map((row) => (
              <tr key={row[0]} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {row.map((cell, i) => (
                  <td key={i} className={`px-4 py-3 ${i === 0 ? 'text-left font-semibold whitespace-nowrap' : 'text-right'} ${i === row.length - 1 ? 'font-semibold' : ''}`} style={{ color: i === 0 || i === row.length - 1 ? colors.teal : colors.white, opacity: i === 0 || i === row.length - 1 ? 1 : 0.8 }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Note title="Key takeaway">
        Delivery performance directly affects the final Contribution Index. Strong delivery can improve a mine’s position within the yield schedule, while under-delivery or late delivery reduces the Annual Yield outcome.
      </Note>

      <div className="mt-4 rounded-xl p-5 sm:p-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderLeft: `3px solid ${colors.teal}` }}>
        <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: colors.teal }}>Participation Requirement</p>
        <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.75 }}>
          Failure to meet contracted bullion delivery obligations during a quarterly assessment period will result in the forfeiture of that quarter's Annual Yield allocation. Participation eligibility resets at the beginning of the following assessment period, enabling future yield allocations once program requirements are again satisfied.
        </p>
      </div>
    </SectionShell>

    {/* 9 — CONTRIBUTION INDEX vs ANNUAL YIELD (final destination) */}
    <SectionShell id="contribution-index-yield" bg="#040404">
      <SectionHeading
        eyebrow="Reference Table"
        title="Contribution Index vs Annual Yield"
        sub="The table below illustrates how the final Contribution Index translates into the corresponding Annual Yield within the AZ Reserve Mining Participation Program. Final participation is determined by the Contribution Index calculated using the scoring framework explained above."
      />
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)', background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)' }}>
          <div className="grid grid-cols-2 px-5 sm:px-6 py-3 text-[10px] sm:text-[11px] uppercase tracking-[0.16em]" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
            <span style={{ color: colors.white, opacity: 0.55 }}>Contribution Index</span>
            <span className="text-right" style={{ color: colors.white, opacity: 0.55 }}>Annual Yield</span>
          </div>
          {yieldReference.map(([idx, y]) => (
            <div key={idx} className="grid grid-cols-2 px-5 sm:px-6 py-3 text-sm sm:text-base" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ color: colors.white, opacity: 0.82 }}>{idx}</span>
              <span className="text-right font-semibold tabular-nums" style={{ color: colors.white, opacity: 0.92 }}>{y}</span>
            </div>
          ))}
          {/* Final / maximum row — teal emphasis */}
          <div
            data-testid="mpp-yield-max-row"
            className="grid grid-cols-2 items-center px-5 sm:px-6 py-4"
            style={{ borderTop: `1px solid ${colors.teal}40`, background: `${colors.teal}14` }}
          >
            <span className="font-semibold" style={{ color: colors.teal }}>130+</span>
            <span className="text-right font-bold tabular-nums flex items-center justify-end gap-2" style={{ color: colors.teal }}>
              15.00%
              <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold" style={{ backgroundColor: `${colors.teal}22`, border: `1px solid ${colors.teal}55` }}>Maximum</span>
            </span>
          </div>
        </div>
        <p className="mt-6 text-center text-sm sm:text-base leading-relaxed font-medium" style={{ color: colors.white, opacity: 0.85 }}>
          Yield increases with contribution performance and is capped at a maximum Annual Yield of 15.00%.
        </p>
      </div>
    </SectionShell>

    {/* FOOTER (simple Arizore footer, no navigation) */}
    <footer style={{ backgroundColor: colors.graphite }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 flex flex-col items-center text-center">
        <img src="/logo-white-footer-new.png" alt="Arizore" className="h-12 sm:h-14 w-auto mb-4" />
        <p className="text-xs sm:text-sm leading-relaxed max-w-md mb-6" style={{ color: colors.white, opacity: 0.6 }}>
          Building a transparent reserve ecosystem for tokenised natural resources.
        </p>
        <div className="w-full border-t pt-6" style={{ borderColor: `${colors.white}20` }}>
          <p className="text-xs sm:text-sm" style={{ color: colors.white, opacity: 0.4 }}>
            © 2025 Arizore. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  </div>
  );
};

// ---------------------------------------------------------------------------
const MiningParticipationProgramPage = () => {
  const [unlocked, setUnlocked] = useState(
    !MINING_PROGRAM.gateEnabled || sessionStorage.getItem(SESSION_KEY) === 'true'
  );

  // Title + noindex/nofollow injected directly (reliable across React 18; react-helmet 6.x does not commit).
  useEffect(() => {
    const prevTitle = document.title;
    const prevScroll = document.documentElement.style.scrollBehavior;
    document.title = 'AZ Reserve Mining Participation Program';
    document.documentElement.style.scrollBehavior = 'smooth';
    let meta;
    if (MINING_PROGRAM.noindex) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex,nofollow,noarchive';
      document.head.appendChild(meta);
    }
    return () => {
      document.title = prevTitle;
      document.documentElement.style.scrollBehavior = prevScroll;
      if (meta && meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [unlocked]);

  return unlocked ? <PageContent /> : <PasswordGate onUnlock={() => setUnlocked(true)} />;
};

export default MiningParticipationProgramPage;
